/**
 * DSRProgressCard — Completeness ring + PIR dimension checklist.
 * Rendered inline in the chat as a component event.
 */

const PIR_LABELS: Record<string, string> = {
  property_basics: "Property Basics",
  tax_analysis: "Tax Analysis",
  site_screening: "Site Screening",
  market_analysis: "Market Analysis",
  owner_research: "Owner Research",
  financial_structuring: "Financial Structuring",
};

const STATUS_ICONS: Record<string, string> = {
  unknown: "○",
  partial: "◐",
  complete: "●",
};

const STATUS_COLORS: Record<string, string> = {
  unknown: "var(--text-dim)",
  partial: "var(--warning)",
  complete: "var(--success)",
};

interface DSRProgressCardProps {
  completeness: number;
  pirStatus: Record<string, string>;
}

export default function DSRProgressCard({
  completeness,
  pirStatus,
}: DSRProgressCardProps) {
  const ringSize = 80;
  const strokeWidth = 6;
  const radius = (ringSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const filled = (completeness / 100) * circumference;
  const ringColor =
    completeness >= 75
      ? "var(--success)"
      : completeness >= 50
        ? "var(--warning)"
        : "var(--accent)";

  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "12px",
        padding: "16px",
        display: "flex",
        gap: "20px",
        alignItems: "flex-start",
      }}
    >
      {/* Ring */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`}>
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={radius}
            fill="none"
            stroke="var(--border)"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={ringSize / 2}
            cy={ringSize / 2}
            r={radius}
            fill="none"
            stroke={ringColor}
            strokeWidth={strokeWidth}
            strokeDasharray={`${filled} ${circumference}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
            style={{ transition: "stroke-dasharray 0.5s ease" }}
          />
        </svg>
        <span
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: "16px",
            fontWeight: 700,
            color: "var(--text-primary)",
          }}
        >
          {completeness}%
        </span>
      </div>

      {/* PIR checklist */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: "12px",
            fontWeight: 600,
            color: "var(--text-muted)",
            marginBottom: "8px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Intelligence Picture
        </div>
        {Object.entries(pirStatus).map(([key, status]) => (
          <div key={key} className="pir-row">
            <span style={{ color: STATUS_COLORS[status] || "var(--text-dim)" }}>
              {STATUS_ICONS[status] || "○"}
            </span>
            <span
              style={{
                fontSize: "13px",
                color:
                  status === "complete"
                    ? "var(--text-secondary)"
                    : "var(--text-dim)",
              }}
            >
              {PIR_LABELS[key] || key}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
